import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { LockKey, ArrowLeft, WhatsappLogo, ChartBar, Pulse, Users, Truck, Compass, ArrowRight } from "@phosphor-icons/react";

const DEMO_SCREENS = {
  "medflow-erp": [
    { icon: Users, label: "Patient CRM", val: "12,482", sub: "Active records" },
    { icon: ChartBar, label: "No-show rate", val: "18.2%", sub: "-9pp vs. last quarter" },
    { icon: Pulse, label: "Claims cycle", val: "35 days", sub: "-10 days" },
  ],
  "retailpulse": [
    { icon: ChartBar, label: "AOV", val: "₹1,214", sub: "+18% MoM" },
    { icon: Pulse, label: "Inventory accuracy", val: "96.4%", sub: "44 stores live" },
    { icon: Users, label: "Loyalty members", val: "82,113", sub: "+2,140 this week" },
  ],
  "logistack": [
    { icon: Truck, label: "Avg. deliveries / driver", val: "31", sub: "+5 vs baseline" },
    { icon: Pulse, label: "On-time SLA", val: "94.1%", sub: "Rolling 7 days" },
    { icon: ChartBar, label: "Cost per drop", val: "₹42", sub: "-11%" },
  ],
  "eduorbit": [
    { icon: Users, label: "Parent adoption", val: "92%", sub: "60-day cohort" },
    { icon: ChartBar, label: "Fee collection cycle", val: "6 days", sub: "-15 days" },
    { icon: Pulse, label: "LMS DAU", val: "37,208", sub: "This week" },
  ],
};

export default function ProductDemo() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [gated, setGated] = useState(true);
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get(`/products/slug/${slug}`).then((r) => setProduct(r.data)).catch(() => {});
    const stored = JSON.parse(localStorage.getItem("maxine_demo_tokens") || "{}");
    const token = stored[slug];
    if (!token) { nav(`/products/${slug}?demo=1`, { replace: true }); return; }
    api.get("/demo-verify", { params: { token, product_slug: slug } })
      .then((r) => { setEmail(r.data.email); setGated(false); })
      .catch(() => nav(`/products/${slug}?demo=1`, { replace: true }));
  }, [slug, nav]);

  const screens = (product?.demo_stats && product.demo_stats.length > 0)
    ? product.demo_stats.map((s, i) => ({ ...s, icon: [ChartBar, Pulse, Users, Truck][i % 4] }))
    : (DEMO_SCREENS[slug] || []);

  if (gated || !product) return <div className="min-h-screen bg-white" />;

  return (
    <div className="min-h-screen bg-[color:var(--paper-alt)]">
      <SEO title={`${product.title} — Live demo`} description={`Private live demo of ${product.title}`} />
      <Nav />
      <main className="pt-28 md:pt-36 pb-20">
        <div className="container-x">
          <Link to={`/products/${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-500 hover:text-[color:var(--brand)]">
            <ArrowLeft size={14} weight="bold" /> Back to {product.title}
          </Link>

          <div data-testid="demo-header" className="mt-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="eyebrow flex items-center gap-2"><LockKey size={12} weight="bold" /> Private demo · {email}</div>
              <h1 className="font-display font-black text-4xl md:text-5xl mt-3 leading-[1.05]">{product.title} — live snapshot</h1>
              <p className="mt-3 text-neutral-600 max-w-2xl">A read-only slice of a production {product.title} tenant. Numbers refresh nightly and are pulled from anonymised aggregate data.</p>
            </div>
            <a href={`https://wa.me/7054986771?text=${encodeURIComponent(`I saw the ${product.title} demo — want to book a walkthrough.`)}`} target="_blank" rel="noreferrer" className="btn-brand">
              <WhatsappLogo size={16} weight="fill" /> Book a live walkthrough
            </a>
          </div>

          <div className="mt-6 border-2 border-[color:var(--brand)] bg-[color:var(--brand)]/5 p-6 md:p-8 flex flex-wrap items-center gap-6 justify-between">
            <div className="flex items-center gap-4">
              <Compass size={28} weight="bold" className="text-[color:var(--brand)]" />
              <div>
                <div className="eyebrow">Want more than a snapshot?</div>
                <div className="mt-1 font-display font-bold text-xl">Take the interactive click-through tour</div>
                <div className="text-sm text-neutral-600 mt-1">Step through the workflow in a guided walk-through with live UI previews.</div>
              </div>
            </div>
            <Link to={`/products/${slug}/tour`} data-testid="open-tour" className="btn-brand text-sm">
              Start interactive tour <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {screens.map((s) => {
              const I = s.icon;
              return (
                <div key={s.label} data-testid={`demo-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-[color:var(--line)] p-6">
                  <div className="flex items-center justify-between">
                    <I size={18} weight="bold" className="text-[color:var(--brand)]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Live</span>
                  </div>
                  <div className="mt-4 font-display font-black text-4xl">{s.val}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{s.label}</div>
                  <div className="mt-2 text-sm text-neutral-600">{s.sub}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-[color:var(--line)] p-6">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">7-day activity</div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Aggregate</span>
              </div>
              <svg viewBox="0 0 400 160" className="mt-6 w-full">
                <defs>
                  <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#1E88F0" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#1E88F0" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0 130 L60 90 L120 100 L180 60 L240 80 L300 40 L360 55 L400 30 L400 160 L0 160 Z" fill="url(#g)" />
                <path d="M0 130 L60 90 L120 100 L180 60 L240 80 L300 40 L360 55 L400 30" fill="none" stroke="#1E88F0" strokeWidth="2" />
                {[0, 60, 120, 180, 240, 300, 360, 400].map((x, i) => (
                  <circle key={i} cx={x} cy={[130, 90, 100, 60, 80, 40, 55, 30][i]} r="3" fill="#050505" />
                ))}
              </svg>
            </div>
            <div className="bg-[color:var(--brand)]/5 border border-[color:var(--brand)] p-6">
              <div className="eyebrow">Want the real thing?</div>
              <p className="mt-3 text-neutral-700 text-sm leading-relaxed">This snapshot is anonymised. Book a live walkthrough and we&apos;ll show you the full admin — including workflows, reports and integrations.</p>
              <a href={`https://wa.me/7054986771?text=${encodeURIComponent(`Book ${product.title} walkthrough`)}`} target="_blank" rel="noreferrer" className="mt-6 btn-brand w-full justify-center text-sm">
                <WhatsappLogo size={14} weight="fill" /> Chat on WhatsApp
              </a>
            </div>
          </div>

          <div className="mt-6 bg-white border border-[color:var(--line)] p-6">
            <div className="font-display font-bold text-lg">Included modules</div>
            <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(product.features || []).map((f) => (
                <div key={f} className="border border-[color:var(--line)] p-4 flex items-center gap-3">
                  <span className="w-2 h-2 bg-[color:var(--brand)]" /> <span className="text-sm text-neutral-700">{f}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-10 text-xs text-neutral-500 font-mono">Your demo link expires 24 hours after you first opened it. Refresh access anytime by requesting the demo again.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
